import { t } from '@bia-auction/shared-i18n';
import { BrandLogo, Button, cn, ThemeToggle } from '@bia-auction/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useRouter } from '@tanstack/react-router';
import {
  Building2,
  Car,
  Gavel,
  Inbox,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  Settings,
  Users,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth';
import { BRAND } from '../lib/config';
import { canOpen } from '../lib/roles';

const NAV_KEY = 'bia.admin.nav';

/**
 * Authenticated layout (redesign-admin.md §1): collapsible left sidebar — small
 * lockup on top (icon when collapsed), role-gated nav, logout at the bottom —
 * with the routed content in a fluid main region.
 */
export function AppShell() {
  const { state, logout } = useAuth();
  const router = useRouter();
  const qc = useQueryClient();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(NAV_KEY) === 'collapsed');

  // The route guard only checks that a refresh token exists; if AuthProvider's boot-time
  // `ensureAccess` finds it stale/revoked it clears the session, so redirect reactively rather
  // than leaving the operator on the shell with failing queries.
  useEffect(() => {
    if (!state.authenticated) void router.navigate({ to: '/login' });
  }, [state.authenticated, router]);

  // Drop the shared React Query cache whenever the authenticated identity OR its role set
  // changes, so neither a second operator nor a same-user permission change can leave the
  // previous (possibly more-privileged) cached data visible. A `/auth/refresh` can return the
  // same `sub` with a downgraded `roles` claim; without the role fingerprint the nav would
  // update from `state.roles` but manager-only cached fields (reserve, incrementBands, hostUrl)
  // would survive until each query refetched. Roles are sorted so ordering never false-triggers.
  const identityKey = `${state.userId ?? ''}|${[...state.roles].sort().join(',')}`;
  const lastIdentity = useRef<string>(identityKey);
  // Identity-change armor, reworked. clear() removes query objects WITHOUT notifying
  // their subscribed observers, so any route whose queries were in flight is left on a
  // forever-pending hook — and on every full page load the boot-time session restore
  // flips the identity (null → user) right after the route's queries start, which froze
  // stable-key pages (/config) for good.
  //
  // The null → user transition needs NO wipe: a fresh page load starts from an empty
  // cache and its pre-identity queries already ran with the restoring session's token,
  // and the relogin path is covered by LoginPage clearing the cache on mount. Only a
  // REAL switch (previous identity had a userId: another operator, or the same operator
  // with a changed role claim) wipes — clear() for the security property (nothing from
  // the previous identity stays renderable, not even as stale data) plus a remount of
  // the routed content via the epoch key, so orphaned observers re-subscribe fresh.
  const [identityEpoch, setIdentityEpoch] = useState(0);
  useEffect(() => {
    if (lastIdentity.current !== identityKey) {
      const hadUser = lastIdentity.current.split('|')[0] !== '';
      lastIdentity.current = identityKey;
      if (hadUser) {
        qc.clear();
        setIdentityEpoch((e) => e + 1);
      }
      // Re-run the route guards against the new roles (M19) — on **every** identity change,
      // including the first. `beforeLoad` fires on navigation, and neither a role downgrade
      // nor a late-arriving identity brings one.
      //
      // The null → user case matters because `requireRoute` deliberately lets a route through
      // when a cold-load refresh fails transiently: it has no roles to judge by, and stranding
      // an authorized operator on /forbidden would be worse. Once the identity does arrive,
      // that permissive answer has to be revisited, or someone who may not open the page stays
      // on it watching its queries 403.
      //
      // Outside the `hadUser` branch on purpose: this only replays the guards, while the cache
      // wipe and remount above are reserved for a real switch between identities (see the
      // comment there — wiping on the boot-time transition used to freeze stable-key pages).
      void router.invalidate();
    }
  }, [identityKey, qc, router]);

  // `show` is derived from `ROUTE_PERMISSIONS` (M19) — the same map the router's guards read,
  // so an entry can no longer be hidden here while its route opens by URL, or the reverse.
  // Which destinations appear at all, and in what order, stays a decision for this file.
  // `exact` on "/": it prefixes every other route, so without it the overview would render as
  // the active entry on every page.
  const nav = [
    { to: '/' as const, label: t('nav.dashboard'), icon: LayoutDashboard, exact: true },
    { to: '/sales' as const, label: t('nav.sales'), icon: Gavel },
    { to: '/vehicles' as const, label: t('nav.vehicles'), icon: Car },
    { to: '/submissions' as const, label: t('nav.submissions'), icon: Inbox },
    { to: '/firms' as const, label: t('nav.firms'), icon: Building2 },
    { to: '/users' as const, label: t('nav.users'), icon: Users },
    { to: '/audit' as const, label: t('nav.audit'), icon: ScrollText },
    { to: '/config' as const, label: t('nav.config'), icon: Settings },
    { to: '/help' as const, label: t('nav.help'), icon: LifeBuoy },
  ].filter((n) => canOpen(state.roles, n.to));

  const onToggle = () => {
    setCollapsed((c) => {
      localStorage.setItem(NAV_KEY, c ? 'expanded' : 'collapsed');
      return !c;
    });
  };

  const onLogout = () => {
    logout();
    qc.clear();
    void router.navigate({ to: '/login' });
  };

  return (
    <div className="flex min-h-screen">
      <aside
        className={cn(
          'sticky top-0 flex h-screen flex-col border-r bg-surface transition-[width]',
          collapsed ? 'w-14' : 'w-60',
        )}
      >
        <div className={cn('flex h-14 items-center gap-2 border-b px-3', collapsed && 'justify-center px-0')}>
          {/* The wordmark ink is unreadable on navy — swap to the white variant whenever the
              dark theme is in effect (docs/15 §3). */}
          {collapsed ? (
            <>
              <BrandLogo tier="icon" alt={BRAND} className="dark:hidden" />
              <BrandLogo tier="icon" variant="white" alt={BRAND} className="hidden dark:block" />
            </>
          ) : (
            <>
              <BrandLogo alt={BRAND} className="dark:hidden" />
              <BrandLogo variant="white" alt={BRAND} className="hidden dark:block" />
              {/* The lockup already spells the brand — only the product qualifier here. */}
              <span className="truncate text-sm font-semibold">{t('app.nav.title')}</span>
            </>
          )}
        </div>
        <nav className={cn('flex flex-1 flex-col gap-1 overflow-y-auto p-2', collapsed && 'items-center')}>
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              activeOptions={n.exact ? { exact: true } : undefined}
              title={collapsed ? n.label : undefined}
              aria-label={collapsed ? n.label : undefined}
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&.active]:bg-active [&.active]:font-medium [&.active]:text-tx-brand',
                collapsed && 'justify-center px-2',
              )}
            >
              <n.icon aria-hidden className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{n.label}</span>}
            </Link>
          ))}
        </nav>
        <div className={cn('flex flex-col gap-1 border-t p-2', collapsed && 'items-center')}>
          <Button
            variant="ghost"
            size={collapsed ? 'icon' : 'sm'}
            aria-label={t('nav.toggle')}
            title={t('nav.toggle')}
            onClick={onToggle}
            className={cn(!collapsed && 'justify-start')}
          >
            {collapsed ? (
              <PanelLeftOpen aria-hidden className="h-4 w-4" />
            ) : (
              <PanelLeftClose aria-hidden className="h-4 w-4" />
            )}
            {!collapsed && t('nav.toggle')}
          </Button>
          <ThemeToggle showLabel={!collapsed} />
          <Button
            variant="ghost"
            size={collapsed ? 'icon' : 'sm'}
            title={collapsed ? t('common.logout') : undefined}
            aria-label={collapsed ? t('common.logout') : undefined}
            onClick={onLogout}
            className={cn(!collapsed && 'justify-start')}
          >
            <LogOut aria-hidden className="h-4 w-4" />
            {!collapsed && t('common.logout')}
          </Button>
        </div>
      </aside>
      <main key={identityEpoch} className="min-w-0 flex-1 px-8 py-6">
        <Outlet />
      </main>
    </div>
  );
}
